import { inject, Injectable, OnDestroy } from "@angular/core";
import { BehaviorSubject, filter, Observable, of, Subject, Subscription, switchMap, throwError } from "rxjs";
import { HttpClient } from "@angular/common/http";
import { environment } from "../../environments/environment";
import { ApiService } from "./api.service";
import { catchError, tap, takeUntil, debounceTime, finalize } from "rxjs/operators";
import { CoreService } from "./core.service";
import { LoaderService } from "./loader.service";
import { Bet } from "../types/bet.type";
import { MarketService } from "./market.service";
import { MarketData } from "../types/market.type";
import { ToasterService } from "./toaster.service";

@Injectable({
  providedIn: "root",
})
export class BetPlacementService implements OnDestroy {
  readonly subscriptions: Subscription = new Subscription();
  selectedParams = new BehaviorSubject<any>(null);
  loader = inject(LoaderService);
  apiService = inject(ApiService);
  openBetsSubject: BehaviorSubject<any> = new BehaviorSubject<any>([]);
  currentPlacement: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  openBetFilterParams: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  refreshBets$ = new BehaviorSubject<boolean>(false);
  coreService = inject(CoreService);
  private changeNotifier = new Subject<any>();
  changeNotifier$ = this.changeNotifier.asObservable();
  private locale = this.coreService.getLocale();
  disablePlacement$ = new BehaviorSubject<boolean>(false);
  toaster = inject(ToasterService);
  marketService = inject(MarketService);
  handicapMarketsForPnLCalc$ = new BehaviorSubject<Map<string, MarketData>>(new Map());
  unmatchedBets$ = new BehaviorSubject<string[]>([]);
  isCancellingMultiple: boolean = false;
  private destroy$ = new Subject<void>();

  constructor(private http: HttpClient) {
    this.refreshBets$
      .pipe(
        filter((refresh) => refresh), // Proceed only if refresh is true
        debounceTime(200),
        tap(() => {
          this.setRefresh(false);
          this.disablePlacement$.next(true);
        }), // Set refresh to false after triggering
        switchMap(() => this.fetchOpenBets({ locale: this.locale })),
        takeUntil(this.destroy$),
      )
      .subscribe({
        next: (data) => {
          this.openBetsSubject.next(data?.data || []);
          this.disablePlacement$.next(false);
        },
        error: (error) => {
          console.error("Error fetching open bets", error);
          this.openBetsSubject.next([]);
          this.disablePlacement$.next(false);
        },
      });

    this.unmatchedBets$.pipe(takeUntil(this.destroy$)).subscribe({
      next: (data) => {
        if (!data?.length && this.isCancellingMultiple) {
          this.toaster.success("Success", "All bet cancellations submitted successfully.");
          this.isCancellingMultiple = false;
          this.setRefresh(true);
        }
      },
    });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();

    // Clear all BehaviorSubjects to free memory
    this.selectedParams.next(null);
    this.openBetsSubject.next([]);
    this.currentPlacement.next(null);
    this.openBetFilterParams.next(null);
    this.refreshBets$.next(false);
    this.disablePlacement$.next(false);
    this.handicapMarketsForPnLCalc$.next(new Map());
    this.unmatchedBets$.next([]);

    // Clear change notifier
    this.changeNotifier.complete();
  }

  setUnmatchedBets(betIds: string | string[]) {
    const currentIds = this.unmatchedBets$.getValue();
    const updatedIds = Array.isArray(betIds) ? (betIds as string[]) : [...currentIds].filter((id) => id !== betIds);
    this.unmatchedBets$.next(updatedIds);
  }

  getMarketsForACHBets(bets: Bet[]) {
    if (!bets.length) return;
    const asianHandicapMarkets = bets.filter((bet) => bet.bettingType?.includes("ASIAN_HANDICAP"));
    if (!asianHandicapMarkets.length) return;
    const uniqueMarketIds = Array.from(new Set(asianHandicapMarkets.map((m) => m.marketId)));

    this.marketService
      .requestMarket({ ids: uniqueMarketIds })
      .pipe(takeUntil(this.destroy$))
      .subscribe((response: { data?: any[] }) => {
        if (response.data?.length) {
          const markets = new Map(response?.data?.map((market: any) => [market.marketId, market]));
          this.handicapMarketsForPnLCalc$.next(markets);
        }
      });
  }

  checkBetStatus(betId: number) {
    this.subscriptions.add(
      this.fetchBetStatus({ betId }).subscribe({
        next: (response: any) => {
          if (response?.data) {
            this.toaster.success("Placement Successful", `Bet ID: ${betId} was successfully placed.`);
          } else {
            this.toaster.error("Error", `Bet ID: ${betId} Placement timeout!`);
          }
        },
        error: (error) => {
          console.log(error);
          this.toaster.error("Error", `Bet ID: ${betId} Placement timeout!`);
        },
      }),
    );
  }

  fetchOpenBets(params: any) {
    return this.http
      .get<any>(`${environment.mURL}${environment.endpoints.bfOrderStatus}`, {
        params,
      })
      .pipe(
        catchError((error) => {
          console.error("Error fetching open bets:", error);
          this.openBetsSubject.next([]);
          return of([]); // Ensure the stream continues
        }),
      );
  }

  fetchBetStatus(params: any) {
    return this.http.get<any>(`${environment.mURL}${environment.endpoints.bfOrderStatus}/${params.betId}`).pipe(
      catchError((error) => {
        console.error("Error fetching open bets:", error);
        return of(null);
      }),
    );
  }

  notifyChange(type: any) {
    if (type === "multipleCancellation") this.isCancellingMultiple = true;
    this.changeNotifier.next(type);
  }

  setRefresh(value: boolean) {
    this.refreshBets$.next(value);
  }

  setParams(data: any) {
    const userData = this.apiService.getUserToken();
    if (userData?.user?.IS_BLOCKED === "false") {
      this.selectedParams.next(data);
    }
  }

  getParams() {
    return this.selectedParams.asObservable();
  }

  setOpenBetFilterParams(value: any) {
    this.openBetFilterParams.next(value);
  }

  private readonly PLACING_BET_KEY = "placing_bet_flag";

  performPlacement(body: any, source: "cash-out" | "placement" = "placement"): Observable<any> {
    if (source === "placement") {
      // Check cross-tab protection using localStorage
      const isPlacingInAnotherTab = localStorage.getItem(this.PLACING_BET_KEY) === "true";

      // Global protection against concurrent bet placement (same tab + cross-tab)
      if (isPlacingInAnotherTab) {
        return throwError(() => new Error("Another bet is already processing"));
      }

      localStorage.setItem(this.PLACING_BET_KEY, "true");
    }

    return this.http
      .post(`${environment.sURL}${environment.endpoints.placeBet}`, body, {
        headers: this.apiService.getHeaders(),
      })
      .pipe(
        finalize(() => {
          setTimeout(() => {
            localStorage.removeItem(this.PLACING_BET_KEY);
          }, 5000);
        }),
      );
  }

  updatePlacement(body: any): Observable<any> {
    return this.http.post(`${environment.sURL}${environment.endpoints.updateBet}`, body, {
      headers: this.apiService.getHeaders(),
    });
  }

  getOpenBetsObservable(): Observable<any[]> {
    return this.openBetsSubject.asObservable();
  }

  getCurrentPlacement(): Observable<any> {
    return this.currentPlacement.asObservable();
  }

  cancelBet(params?: any) {
    return this.http.post(`${environment.sURL}${environment.endpoints.cancelBet}`, params);
  }

  fetchPendingBets() {
    return this.http.get(`${environment.mURL}${environment.endpoints.bfOrderStatus}`);
  }

  private buildUrlWithParams(params?: { page?: number; size?: number; sortField?: string; sortDir?: string }): string {
    const basePath = `${environment.sURL}${environment.endpoints.myBets}`;
    let path = "";

    // Check each param and add to the path only if it exists
    if (params) {
      const { page, size, sortField, sortDir } = params;
      path = [
        page != null ? page : "",
        size != null ? size : "",
        sortField != null ? encodeURIComponent(sortField) : "",
        sortDir != null ? encodeURIComponent(sortDir) : "",
      ]
        .filter((part) => part !== "")
        .join("/");
    }

    return `${basePath}/${path}`;
  }

  fetchBetsByStatus(params?: any) {
    return this.http.post(`${environment.sURL}${environment.endpoints.myBetsByStatus}`, params);
  }

  updateBetSlipSettings(body: any): Observable<any> {
    return this.http.post(`${environment.sURL}${environment.endpoints.updateBetSlipSettings}`, body, {
      headers: this.apiService.getHeaders(),
    });
  }

  getSlipSettings() {
    const app = this.apiService.getUserToken();
    const slipSettings = app?.user?.USER_BETSLIP;

    if (slipSettings !== null && slipSettings !== undefined && slipSettings !== "null" && slipSettings !== "undefined") {
      return JSON.parse(slipSettings);
    }

    return null;
  }

  getWLSlipSettings() {
    const app = this.apiService.getUserToken();
    const slipSettings = app?.user?.WHITE_LABEL_BET_SLIP;

    if (slipSettings !== null && slipSettings !== undefined && slipSettings !== "null" && slipSettings !== "undefined") {
      return JSON.parse(slipSettings);
    }

    return null;
  }

  currentPlacementConcernsMe(items: any, compareField: string) {
    const params = this.selectedParams.value;
    if (!params?.data) return;
    const data = params?.data;
    const paramKey = compareField === "marketId" ? "MARKET_ID" : "EVENT_ID";

    if (Array.isArray(items)) {
      const hasConcernedItems = items.some((item: any) => {
        return item[compareField] === data[paramKey];
      });
      if (!hasConcernedItems) {
        this.setParams(null);
      }
    } else {
      const isItemConcerned = items[compareField] === data[paramKey];
      if (!isItemConcerned) {
        this.setParams(null);
      }
    }
  }

  get persistenceType() {
    const userSlipSettings = this.getSlipSettings();
    return userSlipSettings?.betSlipSettings?.persistenceType ?? "LAPSE";
  }
}
